/**
 * promptsCatalog.ts
 *
 * Master catalog of all prompt files, grouped by agent / function.
 * Used by AdminPromptsPage and the ASB task-prompt list.
 *
 * Keep in sync with backend/prompts/ after any addition or deletion.
 */

export interface PromptEntry {
  file: string;
  description: string;
  shared?: boolean; // true = also used by another agent
}

export interface PromptGroup {
  id: string;
  label: string;          // plain label (i18n handled at call site if needed)
  color: string;          // accent color for the group badge
  prompts: PromptEntry[];
}

export const PROMPT_CATALOG: PromptGroup[] = [

  // ── Pilot & session layer ───────────────────────────────────────────────
  {
    id: 'pilot',
    label: 'Pilot & session',
    color: '#1971c2',
    prompts: [
      { file: 'pilot_system_prompt.txt',        description: 'Pilot orchestrator — guides the 7-step SP workflow' },
      { file: 'sp_session_digest_prompt.txt',   description: 'Summarises a WS session into a 1–2 sentence digest' },
      { file: 'sp_general_state_prompt.txt',    description: 'Synthesises recent digests into a workflow state snapshot' },
      { file: 'sp_resume_briefing_prompt.txt',  description: 'Generates the "where were we?" briefing shown on SP open' },
      { file: 'scv2_project_system_prompt.txt',     description: 'System prompt for the SCV2 chatbot on a Project page' },
      { file: 'scv2_sub_project_system_prompt.txt', description: 'System prompt for the SCV2 chatbot on a Sub-project page' },
      { file: 'speaking_chatbot_system_prompt.txt', description: 'System prompt for the KB-backed speaking chatbot' },
    ],
  },

  // ── State-of-the-Art CFA ───────────────────────────────────────────────
  {
    id: 'sota',
    label: 'State-of-the-Art CFA',
    color: '#2f9e44',
    prompts: [
      { file: 'sota_cfa_system_prompt.txt',       description: 'SOTA CFA — 5-step state-of-the-art workflow guide' },
      { file: 'sota_scoping_note_prompt.txt',     description: 'Generates the preliminary ungrounded SOTA scaffold' },
      { file: 'sota_coherence_pass_prompt.txt',   description: 'Verifies grounding and flags unsupported claims' },
    ],
  },

  // ── Solution Description CFA ───────────────────────────────────────────
  {
    id: 'solution_desc',
    label: 'Solution Description CFA',
    color: '#7950f2',
    prompts: [
      { file: 'solution_description_cfa_system_prompt.txt', description: 'Solution Description CFA — guides solution field editing' },
      { file: 'features_list_generation_task_prompt.txt',   description: 'Generates a structured feature list from the solution description' },
    ],
  },

  // ── Problem Statement CFA ───────────────────────────────────────────────
  {
    id: 'problem_statement',
    label: 'Problem Statement CFA',
    color: '#f59f00',
    prompts: [
      { file: 'problem_statement_cfa_system_prompt.txt', description: 'Problem Statement CFA — guides problem statement definition' },
    ],
  },

  // ── Claims CFA ─────────────────────────────────────────────────────────
  {
    id: 'claims',
    label: 'Claims CFA',
    color: '#e03131',
    prompts: [
      { file: 'claims_cfa_system_prompt.txt',            description: 'Claims CFA — guides the full claim drafting pipeline' },
      { file: 'suggest_claimable_items_prompt.txt',      description: 'Suggests claimable items from the solution description' },
      { file: 'claimable_item_patent_score_prompt.txt',  description: 'Scores each claimable item against prior art' },
      { file: 'list_claimable_aspects_prompt.txt',       description: 'Lists claimable technical aspects of one item' },
      { file: 'homogenize_aspects_prompt.txt',           description: 'Normalises aspect terminology before drafting' },
      { file: 'draft_independent_claim_prompt.txt',      description: 'Drafts the independent claim for one item' },
      { file: 'draft_dependent_claims_prompt.txt',       description: 'Drafts dependent claims (one per aspect)' },
      { file: 'draft_claims_per_item_prompt.txt',        description: 'Full independent + dependent claim set for one item' },
      { file: 'draft_solution_claims_prompt.txt',        description: 'Drafts an initial claim set for the whole solution' },
      { file: 'homogenize_claims_wording_prompt.txt',    description: 'Standardises terminology and antecedent basis across all claims' },
      { file: 'invention_summary_generation_prompt.txt', description: 'Generates a high-level invention summary for claims context' },
    ],
  },

  // ── Patentability CFA ──────────────────────────────────────────────────
  {
    id: 'patentability',
    label: 'Patentability CFA',
    color: '#0c8599',
    prompts: [
      { file: 'patentability_cfa_system_prompt.txt',                  description: 'Patentability CFA — guides novelty and inventive step analysis' },
      { file: 'claim_analysis_prompt.txt',                            description: 'Extracts key features and limitations from patent claims', shared: true },
      { file: 'independent_claim_validator_prompt.txt',               description: 'Validates and classifies claims as independent or dependent', shared: true },
      { file: 'feature_target_mapping_prompt.txt',                    description: 'Maps solution features against features in prior art', shared: true },
      { file: 'feature_presence_verdict_clear_functional_prompt.txt', description: 'Verdict prompt for clear functional claim elements', shared: true },
      { file: 'feature_presence_verdict_clear_structural_prompt.txt', description: 'Verdict prompt for clear structural claim elements', shared: true },
      { file: 'feature_presence_verdict_markush_or_alternative_prompt.txt', description: 'Verdict prompt for Markush / alternative claim elements', shared: true },
      { file: 'feature_presence_verdict_means_plus_function_prompt.txt',    description: 'Verdict prompt for means-plus-function claim elements', shared: true },
      { file: 'feature_presence_verdict_vague_or_relative_prompt.txt',      description: 'Verdict prompt for vague or relative claim elements', shared: true },
      { file: 'patentability_technical_effect_and_otp_prompt.txt',   description: 'Analyses technical effect and objective technical problem (OTP)' },
      { file: 'patentability_could_would_prompt.txt',                 description: 'Assesses whether a PHOSITA could/would combine prior art' },
      { file: 'claim_rationale_prose_prompt.txt',                     description: 'Generates prose rationale for novelty / inventive step verdicts', shared: true },
    ],
  },

  // ── FTO CFA ────────────────────────────────────────────────────────────
  {
    id: 'fto',
    label: 'FTO CFA',
    color: '#e67700',
    prompts: [
      { file: 'fto_cfa_system_prompt.txt',               description: 'FTO CFA — guides freedom-to-operate analysis' },
      { file: 'claim_analysis_prompt.txt',               description: 'Extracts features from competitor patent claims', shared: true },
      { file: 'independent_claim_validator_prompt.txt',  description: 'Validates claims in FTO patents', shared: true },
      { file: 'feature_target_mapping_prompt.txt',       description: 'Maps solution features against claim elements (infringement check)', shared: true },
      { file: 'feature_presence_verdict_clear_functional_prompt.txt', description: 'Verdict for clear functional elements', shared: true },
      { file: 'feature_presence_verdict_clear_structural_prompt.txt', description: 'Verdict for clear structural elements', shared: true },
      { file: 'feature_presence_verdict_markush_or_alternative_prompt.txt', description: 'Verdict for Markush / alternative elements', shared: true },
      { file: 'feature_presence_verdict_means_plus_function_prompt.txt',    description: 'Verdict for means-plus-function elements', shared: true },
      { file: 'feature_presence_verdict_vague_or_relative_prompt.txt',      description: 'Verdict for vague or relative elements', shared: true },
      { file: 'claim_rationale_prose_prompt.txt',        description: 'Generates prose rationale for infringement verdicts', shared: true },
    ],
  },

  // ── Industrialization CFA ──────────────────────────────────────────────
  {
    id: 'industrialization',
    label: 'Industrialization CFA',
    color: '#5c7cfa',
    prompts: [
      { file: 'industrialization_cfa_system_prompt.txt', description: 'Industrialization CFA — guides the 4-step industrialization dossier' },
      { file: 'icfa_brief.txt',        description: 'Generates the industrialization brief (key manufacturing considerations)' },
      { file: 'icfa_bom.txt',          description: 'Generates the bill of materials' },
      { file: 'icfa_make_or_buy.txt',  description: 'Make-or-buy analysis for each component' },
      { file: 'icfa_risk_register.txt', description: 'Identifies and scores manufacturing / supply chain risks' },
    ],
  },

  // ── Patent Application CFA (PACFA) ─────────────────────────────────────
  {
    id: 'patent_application',
    label: 'Patent Application CFA',
    color: '#862e9c',
    prompts: [
      { file: 'patent_application_cfa_system_prompt.txt', description: 'PACFA — guides the 7-step patent application drafting pipeline' },
      { file: 'pacfa_structure.txt',                  description: 'Analyses solution structure and organises embodiments' },
      { file: 'pacfa_reference_signs.txt',            description: 'Extracts reference signs from figures and claims' },
      { file: 'pacfa_reference_signs_homogenize.txt', description: 'Homogenises reference sign terminology across sections' },
      { file: 'pacfa_field.txt',                      description: 'Drafts the field of invention section' },
      { file: 'pacfa_background.txt',                 description: 'Drafts the background / prior art section' },
      { file: 'pacfa_summary.txt',                    description: 'Drafts the summary of invention section' },
      { file: 'pacfa_embodiment.txt',                 description: 'Drafts detailed embodiment descriptions' },
      { file: 'pacfa_brief_drawings.txt',             description: 'Drafts the brief description of figures' },
      { file: 'pacfa_title_abstract.txt',             description: 'Drafts the title and abstract' },
      { file: 'pacfa_coherence.txt',                  description: 'Coherence check across all sections' },
      { file: 'pacfa_claim_support.txt',              description: 'Validates that each claim element is supported in the description' },
    ],
  },

  // ── Document search — QGS & Extraction ────────────────────────────────
  {
    id: 'search_qgs',
    label: 'Document Search (QGS)',
    color: '#1098ad',
    prompts: [
      { file: 'extraction_qgs_kws_prompt.txt',  description: 'Generates EPO CQL keyword query for OPS patent search' },
      { file: 'extraction_qgs_bm25_prompt.txt', description: 'Generates BM25 query for hybrid KB search' },
      { file: 'extraction_qgs_frs_prompt.txt',  description: 'Generates FRS (free-text relevance search) query' },
      { file: 'extraction_qgs_vs_prompt.txt',   description: 'Generates vector-search query embedding' },
      { file: 'extraction_context_instructions_state_of_the_art.txt', description: 'QGS context for state_of_the_art field (dynamically injected)' },
      { file: 'extraction_context_instructions_fto.txt',              description: 'QGS context for fto field (dynamically injected)' },
      { file: 'extraction_context_instructions_patentability.txt',    description: 'QGS context for patentability field (dynamically injected)' },
      { file: 'extraction_context_instructions_our_solution.txt',     description: 'QGS context for our_solution field (dynamically injected)' },
      { file: 'extraction_context_instructions_industrialization.txt',description: 'QGS context for industrialization field (dynamically injected)' },
      { file: 'extraction_context_instructions_patent_application.txt','description': 'QGS context for patent_application field (dynamically injected)' },
      { file: 'extraction_doc_analysis_prompt.txt',  description: 'Document-level LLM analysis during extraction pipeline' },
      { file: 'extraction_frs_scoring_prompt.txt',   description: 'Scores FRS hits for relevance to the query intent' },
      { file: 'extraction_re_prompt.txt',            description: 'Re-ranking and evidence extraction from retrieved passages' },
      { file: 'assess_relevance_task_prompt.txt',    description: 'Patent relevance assessment against claim chart features [⚠ needs wiring — see special case]' },
    ],
  },

  // ── Field Update Function (FUF) ────────────────────────────────────────
  {
    id: 'fuf',
    label: 'Field Update Function (FUF)',
    color: '#364fc7',
    prompts: [
      { file: 'field_update_known_solutions_prompt.txt',   description: 'Stage 4 — parses existing field text to extract known solutions' },
      { file: 'field_update_cluster_synthesis_prompt.txt', description: 'Stage 6 — synthesises a name and description for each chunk cluster' },
      { file: 'field_update_rewrite_prompt.txt',           description: 'Stage 8 — rewrites the field with inline citations from selected clusters' },
      { file: 'field_update_refinement_prompt.txt',        description: 'Stage 8 (variant) — rewrites with a user-supplied refinement instruction' },
    ],
  },

  // ── Full KB Review ─────────────────────────────────────────────────────
  {
    id: 'kb_review',
    label: 'Full KB Review',
    color: '#087f5b',
    prompts: [
      { file: 'full_kb_review_enrichment_prompt.txt',  description: 'Extracts enrichment content from relevant documents' },
      { file: 'full_kb_review_summary_prompt.txt',     description: 'Generates a synthesis summary across all reviewed documents' },
    ],
  },

  // ── Relevance Assessment (unified) ─────────────────────────────────────
  {
    id: 'relevance',
    label: 'Relevance Assessment',
    color: '#0c8599',
    prompts: [
      { file: 'relevance_problem_statement_prompt.txt', description: 'Relevance of a document/chunk to the sub-project problem (used by KB Review, semantic search, K-Search agent)' },
      { file: 'relevance_solution_prompt.txt',          description: 'Relevance of a document/chunk to a defined solution' },
    ],
  },

  // ── KSearch agent ──────────────────────────────────────────────────────
  {
    id: 'ksearch',
    label: 'Keyword Search (KSearch)',
    color: '#495057',
    prompts: [
      { file: 'ksearch_agent_system_prompt.txt',    description: 'KSA system prompt — Phase 1 keyword search agent' },
      { file: 'search_agent_system_prompt.txt',     description: 'Search agent system prompt (legacy / alternate entry point)' },
      { file: 'search_v3_agent_prompt.txt',         description: 'SearchV3 agent prompt — guided feature-based search conversation' },
      { file: 'generate_keyword_query.txt',         description: 'Generates EPO CQL keyword query from claim chart features' },
      { file: 'generate_search_query_template.txt', description: 'Generates structured search query template from features' },
      { file: 'generate_semantic_query.txt',        description: 'Generates a natural-language semantic search query' },
    ],
  },

  // ── Sub-project generation ─────────────────────────────────────────────
  {
    id: 'sp_generation',
    label: 'Sub-project generation',
    color: '#a61e4d',
    prompts: [
      { file: 'generate_sub_project_claims_template.txt', description: 'Generates initial claim text for a new sub-project' },
    ],
  },

  // ── CPC classification ─────────────────────────────────────────────────
  {
    id: 'cpc',
    label: 'CPC Classification',
    color: '#5c5f66',
    prompts: [
      { file: 'cpc_loop1_subclass_prompt.txt', description: 'Loop 1 — identifies relevant CPC subclasses from patent text' },
      { file: 'cpc_loop2_group_prompt.txt',    description: 'Loop 2 — refines to specific CPC groups within each subclass' },
    ],
  },

  // ── Document processing ────────────────────────────────────────────────
  {
    id: 'doc_processing',
    label: 'Document processing',
    color: '#343a40',
    prompts: [
      { file: 'bibliographic_data_patent_prompt.txt',   description: 'Extracts structured bibliographic data from a patent document' },
      { file: 'bibliographic_data_scipub_prompt.txt',   description: 'Extracts structured bibliographic data from a scientific publication' },
      { file: 'document_summary_prompt.txt',            description: 'Generates a concise summary of a document' },
      { file: 'ocr_extraction_template.txt',            description: 'OCR extraction template (Mistral OCR integration)' },
    ],
  },

  // ── Tech Watch ─────────────────────────────────────────────────────────
  {
    id: 'tech_watch',
    label: 'Tech Watch',
    color: '#e67700',
    prompts: [
      { file: 'tech_watch_item_score_prompt.txt',     description: 'Scores a single tech-watch item for relevance to the sub-project' },
      { file: 'tech_watch_overall_summary_prompt.txt', description: 'Generates the digest summary across all scored items' },
      { file: 'tech_watch_deep_analysis_prompt.txt',  description: 'Deep analysis of a high-relevance tech-watch item' },
    ],
  },

  // ── Actor Landscape ────────────────────────────────────────────────────
  {
    id: 'actor_landscape',
    label: 'Actor Landscape',
    color: '#2b8a3e',
    prompts: [
      { file: 'actor_landscape_report_system_prompt.txt', description: 'Actor landscape agent — system prompt for report generation' },
      { file: 'actor_landscape_report_task_prompt.txt',   description: 'Generates the actor landscape report for a knowledge base' },
      { file: 'actor_normalization_system_prompt.txt',    description: 'Actor normalisation agent — system prompt' },
      { file: 'actor_normalization_task_prompt.txt',      description: 'Normalises actor names and metadata across documents' },
    ],
  },
];

/**
 * Flat map: filename → group id, for quick lookups.
 */
export const FILE_TO_GROUP: Record<string, string> = {};
for (const g of PROMPT_CATALOG) {
  for (const p of g.prompts) {
    FILE_TO_GROUP[p.file] = g.id;
  }
}

/**
 * Task prompts per CFA agent (agent name → list of entries).
 * Used by AgentInfoPanel to show the "Task prompts" section.
 * Excludes system prompt (shown separately) and shared infrastructure.
 */
export const AGENT_TASK_PROMPTS: Record<string, PromptEntry[]> = {
  sota: [
    { file: 'sota_scoping_note_prompt.txt',   description: 'Generates preliminary SOTA scaffold' },
    { file: 'sota_coherence_pass_prompt.txt', description: 'Verifies grounding, flags unsupported claims' },
  ],
  patentability: [
    { file: 'patentability_technical_effect_and_otp_prompt.txt', description: 'Technical effect & objective technical problem' },
    { file: 'patentability_could_would_prompt.txt',              description: 'Could/would PHOSITA analysis' },
    { file: 'claim_analysis_prompt.txt',                         description: 'Claim feature extraction', shared: true },
    { file: 'independent_claim_validator_prompt.txt',            description: 'Claim classification', shared: true },
    { file: 'feature_target_mapping_prompt.txt',                 description: 'Feature-to-prior-art mapping', shared: true },
    { file: 'claim_rationale_prose_prompt.txt',                  description: 'Verdict rationale prose', shared: true },
  ],
  fto: [
    { file: 'claim_analysis_prompt.txt',              description: 'Claim feature extraction', shared: true },
    { file: 'independent_claim_validator_prompt.txt', description: 'Claim classification', shared: true },
    { file: 'feature_target_mapping_prompt.txt',      description: 'Feature-to-claim mapping (infringement)', shared: true },
    { file: 'claim_rationale_prose_prompt.txt',       description: 'Infringement verdict rationale', shared: true },
  ],
  claims: [
    { file: 'suggest_claimable_items_prompt.txt',      description: 'Suggests claimable items from solution' },
    { file: 'claimable_item_patent_score_prompt.txt',  description: 'Scores items against prior art' },
    { file: 'list_claimable_aspects_prompt.txt',       description: 'Lists claimable aspects of one item' },
    { file: 'draft_independent_claim_prompt.txt',      description: 'Drafts independent claim' },
    { file: 'draft_dependent_claims_prompt.txt',       description: 'Drafts dependent claims' },
    { file: 'draft_claims_per_item_prompt.txt',        description: 'Full claim set per item' },
    { file: 'homogenize_claims_wording_prompt.txt',    description: 'Homogenises claim terminology' },
    { file: 'invention_summary_generation_prompt.txt', description: 'Generates invention summary' },
  ],
  industrialization: [
    { file: 'icfa_brief.txt',         description: 'Industrialization brief' },
    { file: 'icfa_bom.txt',           description: 'Bill of materials' },
    { file: 'icfa_make_or_buy.txt',   description: 'Make-or-buy analysis' },
    { file: 'icfa_risk_register.txt', description: 'Manufacturing risk register' },
  ],
  patent_application: [
    { file: 'pacfa_structure.txt',                  description: 'Structure analysis' },
    { file: 'pacfa_reference_signs.txt',            description: 'Reference sign extraction' },
    { file: 'pacfa_reference_signs_homogenize.txt', description: 'Reference sign homogenisation' },
    { file: 'pacfa_field.txt',                      description: 'Field of invention' },
    { file: 'pacfa_background.txt',                 description: 'Background section' },
    { file: 'pacfa_summary.txt',                    description: 'Summary of invention' },
    { file: 'pacfa_embodiment.txt',                 description: 'Embodiment descriptions' },
    { file: 'pacfa_brief_drawings.txt',             description: 'Brief description of drawings' },
    { file: 'pacfa_title_abstract.txt',             description: 'Title and abstract' },
    { file: 'pacfa_coherence.txt',                  description: 'Coherence check' },
    { file: 'pacfa_claim_support.txt',              description: 'Claim support audit' },
  ],
  solution_desc: [
    { file: 'features_list_generation_task_prompt.txt', description: 'Generates structured feature list from solution' },
  ],
  problem_statement: [],
  pilot:   [],
};
